import React, { memo, useMemo, useState } from 'react';
import { Card, Col, Row, Spin } from 'antd';
import type { TableProps } from 'antd';
import styled from 'styled-components';

import { TENANTS } from 'appConstants';
import { GridRenderer } from 'components/Display/GridRenderer';
import CampaignFilter from 'containers/Analytics/components/CampaignFilter';
import { useAnalyticsContext, useCard, useMetabase } from 'containers/Analytics/hooks';
import { TReferralAnalytics, TReferralTierLadder } from 'containers/Campaign/i18n/TModules';
import { useTenant } from 'hooks';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import MetricsCard from '../../components/MetricsCard';
import { CardsRow } from '../../styles';
import {
  CampaignsEngagementGraph,
  CampaignStackedHistogram,
  CampaignTableGraph,
  CampaignVSGraph
} from '../graphs';
import { BadgeIcon, GameTryIcon, PointsIcon, VoucherIcon } from './OutcomeIcons';
import * as S from './styles';

const OutcomeRowWrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 9px 13px;
  gap: 10px;
  height: 72px;
  background: #f6f6f6;
  border: 1px solid #f5f5f5;
  border-radius: 4px;
  & + & {
    margin-top: 12px;
  }
`;

const OutcomesCard = styled(Card)`
  height: 100%;
  display: flex;
  flex-direction: column;
  .ant-card-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding-top: 8px;
    padding-bottom: 8px;
  }
`;

const OutcomeRowLeft = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  min-width: 0;
`;

const OutcomeIconBox = styled.div`
  width: 54px;
  height: 54px;
  background: #ffffff;
  border-radius: 100px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  & > svg {
    width: 32px;
    height: 32px;
  }
`;

const OutcomeTextBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
`;

const OutcomeLabel = styled.div`
  font-family: 'Roboto', sans-serif;
  font-weight: 500;
  font-size: 18px;
  line-height: 24px;
  color: #262626;
`;

const OutcomeSubLabel = styled.div`
  font-family: 'Roboto', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #262626;
`;

const OutcomeValue = styled.div`
  font-family: 'Roboto', sans-serif;
  font-weight: 700;
  font-size: 24px;
  line-height: 32px;
  color: #262626;
  white-space: nowrap;
`;

interface OutcomeRowProps {
  identifier: string;
  label: string;
  unit: string;
  icon: React.ReactNode;
  filterParams?: any;
}

const OutcomeRow: React.FC<OutcomeRowProps> = ({ identifier, label, unit, icon, filterParams }) => {
  const { isDemoMode } = useAnalyticsContext();
  const { card_id: questionId } = useCard(identifier);
  const dataParams = useMemo(() => filterParams ?? {}, [filterParams]);
  const { data, dataError } = useMetabase(questionId, dataParams);

  const rawCount = data?.data?.rows?.[0]?.[0];
  const count = typeof rawCount === 'number' ? rawCount : 0;
  const formattedCount = count.toLocaleString();
  const isLoading = !data && !dataError && !isDemoMode;

  return (
    <OutcomeRowWrapper data-testid={`outcome-row-${identifier}`}>
      <OutcomeRowLeft>
        <OutcomeIconBox>{icon}</OutcomeIconBox>
        <OutcomeTextBox>
          <OutcomeLabel>{label}</OutcomeLabel>
          <OutcomeSubLabel>
            {formattedCount} {unit}
          </OutcomeSubLabel>
        </OutcomeTextBox>
      </OutcomeRowLeft>
      <OutcomeValue>{isLoading ? <Spin size="small" /> : formattedCount}</OutcomeValue>
    </OutcomeRowWrapper>
  );
};

const HeadlineCard = styled(Card)`
  text-align: center;
  && * {
    text-align: center !important;
  }
  .ant-card-body {
    padding: 2.6rem 1rem;
  }
  .ant-row {
    justify-content: center !important;
    box-shadow: none !important;
    background: transparent !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  .ant-row > * {
    flex-grow: 0 !important;
    max-width: 100%;
  }
  .ant-row > *:after {
    display: none !important;
  }
  .ant-spin-container > div,
  .ant-row > * > div,
  .ant-row > * > div > div {
    margin-left: 0 !important;
    padding-left: 0 !important;
    align-items: center !important;
    justify-content: center !important;
  }
  /* Bigger headline count (mockup-matching) */
  && [class*='AnimatedCount'] {
    font-size: 2.5rem !important;
    line-height: 1.1 !important;
  }
  && [class*='Description'] {
    font-size: 0.875rem !important;
    margin-bottom: 0.75rem !important;
  }
`;

const Referrals = () => {
  const { name: tenantName } = useTenant();
  const { t } = useMultiLanguageTranslation();
  const [filterParams, setFilterParams] = useState({});

  return (
    <>
      <Row
        gutter={[16, 16]}
        style={{ marginTop: 20, marginBottom: 20 }}
        data-testid="referral-performance-section"
      >
        <Col span={12}>
          <HeadlineCard data-testid="referral-performance-total-referrers">
            <CardsRow gutter={[0, 16]}>
              <MetricsCard
                identifier="campaign_referral_total_referrers"
                description={t(`${TReferralAnalytics}.TotalReferrers`)}
                filterParams={filterParams}
                subText={
                  <span style={{ whiteSpace: 'nowrap' }}>
                    <span style={{ color: '#52c41a' }}>↑ 88%</span>
                    <span style={{ color: '#9b9b9b' }}> Active advocates</span>
                  </span>
                }
              />
            </CardsRow>
          </HeadlineCard>
        </Col>
        <Col span={12}>
          <HeadlineCard data-testid="referral-performance-total-referees">
            <CardsRow gutter={[0, 16]}>
              <MetricsCard
                identifier="campaign_referral_total_referees"
                description={t(`${TReferralAnalytics}.TotalReferees`)}
                filterParams={filterParams}
                subText={
                  <span style={{ whiteSpace: 'nowrap' }}>
                    <span style={{ color: '#52c41a' }}>↑ +24%</span>
                    <span style={{ color: '#9b9b9b' }}> YoY Conversion lift</span>
                  </span>
                }
              />
            </CardsRow>
          </HeadlineCard>
        </Col>
      </Row>
      <Card
        data-testid="referral-key-metrics-section"
        title={t(`${TReferralAnalytics}.KeyMetrics`)}
        style={{ margin: '20px 0' }}
        headStyle={{ borderBottom: '0px', padding: '14px 40px 16px 25px' }}
      >
        <Row gutter={[16, 16]} justify="space-between">
          <MetricsCard
            identifier="campaign_referral_total_successful"
            description={t(`${TReferralAnalytics}.TotalSuccessfulReferrals`)}
            helpText={t(`${TReferralAnalytics}.TotalSuccessfulReferralsHelp`)}
            filterParams={filterParams}
            subText={
              <span>
                <span style={{ color: '#52c41a' }}>↑ 22.22%</span>
                <span style={{ color: '#9b9b9b' }}> since previous period</span>
              </span>
            }
          />
          <MetricsCard
            identifier="campaign_referral_completion_rate"
            description={t(`${TReferralAnalytics}.ReferralCompletionRate`)}
            helpText={t(`${TReferralAnalytics}.ReferralCompletionRateHelp`)}
            filterParams={filterParams}
            subText={
              <span>
                <span style={{ color: '#595959' }}>Average time </span>
                <span style={{ color: '#2665ee' }}>2.4 Days</span>
              </span>
            }
          />
          <MetricsCard
            identifier="campaign_referral_engagement_rate"
            description={t(`${TReferralAnalytics}.CustomerEngagementRate`)}
            helpText={t(`${TReferralAnalytics}.CustomerEngagementRateHelp`)}
            filterParams={filterParams}
            subText={
              <span>
                <span style={{ color: '#595959' }}>Share ratio </span>
                <span style={{ color: '#d0021b' }}>1:4.2</span>
              </span>
            }
          />
        </Row>
      </Card>
      <Row gutter={[16, 16]} style={{ marginTop: 20, marginBottom: 20 }}>
        <Col xs={24} lg={14} data-testid="referral-referrers-per-tier-section">
          <GridRenderer perRow={1} gutter={0}>
            <CampaignStackedHistogram
              identifier="campaign_referral_referrers_per_tier"
              title={t(`${TReferralAnalytics}.ReferrersPerTier`)}
              yAxisLabel=""
              filterParams={filterParams}
              legend1={t(`${TReferralAnalytics}.ReferrersColumn`)}
              legend2={t(`${TReferralAnalytics}.RefereesColumn`)}
              hideMenu
            />
          </GridRenderer>
        </Col>
        <Col xs={24} lg={10}>
          <OutcomesCard
            data-testid="referral-outcomes-issued-section"
            title={t(`${TReferralAnalytics}.OutcomesIssued`)}
            headStyle={{ borderBottom: '0px', padding: '10px 40px 0px 25px' }}
          >
            <OutcomeRow
              identifier="campaign_referral_outcome_issued_vouchers"
              label={t(`${TReferralTierLadder}.Voucher`)}
              unit="issued"
              icon={<VoucherIcon />}
              filterParams={filterParams}
            />
            <OutcomeRow
              identifier="campaign_referral_outcome_issued_points"
              label={t(`${TReferralTierLadder}.Points`)}
              unit="distributed"
              icon={<PointsIcon />}
              filterParams={filterParams}
            />
            <OutcomeRow
              identifier="campaign_referral_outcome_issued_badges"
              label={t(`${TReferralTierLadder}.Badge`)}
              unit="milestones reached"
              icon={<BadgeIcon />}
              filterParams={filterParams}
            />
            <OutcomeRow
              identifier="campaign_referral_outcome_issued_game_try"
              label={t(`${TReferralTierLadder}.GameTry`)}
              unit="tries issued"
              icon={<GameTryIcon />}
              filterParams={filterParams}
            />
          </OutcomesCard>
        </Col>
      </Row>
      <CampaignFilter onChange={params => setFilterParams(params)} campaignType={'referral'} />
      <Card style={{ margin: '20px 0px 20px 0px' }}>
        <S.CardHeader>Key Metrics</S.CardHeader>
        <CardsRow gutter={[0, 16]}>
          <MetricsCard
            identifier="campaign_referral_customer_engagement"
            description="CUSTOMER ENGAGEMENTS"
            helpText="Total unique customer engagement for refferals campaigns."
            filterParams={filterParams}
          />
        </CardsRow>
      </Card>
      {/* TODO. Make it working correctly */}
      {/* <GridRenderer perRow={1} gutter={16}>
        <Cards md={24}>
          <ReferralsBI filterParams={filterParams} />
        </Cards>
      </GridRenderer> */}
      <CampaignsEngagementGraph
        identifier={[
          'campaign_referral_breakdown_by_date_engagement',
          'campaign_referral_breakdown_by_date_view',
          'campaign_referral_breakdown_by_date_completion'
        ]}
        filterParams={filterParams}
      />
      <Card style={{ margin: '20px 0px 20px 0px' }}>
        <S.CardHeader>Summary</S.CardHeader>
        <CardsRow gutter={[0, 16]}>
          <MetricsCard
            identifier="campaign_referral_total_active_campaign"
            description="TOTAL ACTIVE CAMPAIGNS"
            helpText="Total number of current active campaigns"
            filterParams={filterParams}
          />
          <MetricsCard
            identifier="campaign_referral_total_unique_view"
            description="TOTAL UNIQUE VIEWS"
            helpText="Number of unique users that viewed your campaign"
            filterParams={filterParams}
          />
          <MetricsCard
            identifier="campaign_referral_customer_engagement_rate"
            description="ENGAGEMENT RATE"
            helpText="Percentage of audience reached who completed the campaign"
            filterParams={filterParams}
          />
        </CardsRow>
      </Card>
      <GridRenderer perRow={1} gutter={16}>
        <S.Cards md={24}>
          <ReferralsTopByEngagementRateBI filterParams={filterParams} />
        </S.Cards>
      </GridRenderer>
      <S.SectionHeader>Customer Insights</S.SectionHeader>
      <GridRenderer perRow={1} gutter={16}>
        <CampaignVSGraph
          identifier="campaign_referral_new_vs_returning_user"
          title="New vs Returning Users"
          helpText="Percentages of first time users vs returning users"
          filterParams={filterParams}
        />
      </GridRenderer>
      {tenantName !== TENANTS.RAZER && (
        <GridRenderer perRow={2} gutter={16}>
          <CampaignStackedHistogram
            identifier="campaign_referral_age"
            title="Age"
            filterParams={filterParams}
          />
          <CampaignStackedHistogram
            identifier="campaign_referral_gender"
            title="Gender"
            filterParams={filterParams}
          />
          <ReferralsStateBI filterParams={filterParams} />
          <ReferralsClusterBI filterParams={filterParams} />
        </GridRenderer>
      )}
    </>
  );
};

const ReferralsTopByEngagementRateBI = ({ filterParams = {} }) => {
  const tableProps: TableProps<any> = {
    rowKey: (r: any) => r[0],
    scroll: { x: 1500 },
    columns: [
      {
        title: 'Campaign Name',
        key: 'campaign_name',
        dataIndex: '[1]'
      },
      {
        title: (
          <TableHeader title="Total Views" tooltip="Total number views for Referrals campaign" />
        ),
        key: 'total_views',
        dataIndex: '[2]',
        render: (text, record) => <span>{record[2]?.toLocaleString()}</span>
      },
      {
        title: <TableHeader title="Audience Size" tooltip="Total reach for Referrals campaign" />,
        key: 'total_reach',
        dataIndex: '[3]',
        render: (text, record) => <span>{record[3]?.toLocaleString()}</span>
      },
      {
        title: (
          <TableHeader
            title="Enrolled"
            tooltip="Total number of users enrolled in Referrals campaign"
          />
        ),
        key: 'user_enrolled',
        dataIndex: '[4]',
        render: (text, record) => <span>{record[4]?.toLocaleString()}</span>
      },
      {
        title: (
          <TableHeader
            title="Unique Views"
            tooltip="Total number of unique views for Referrals campaign"
          />
        ),
        key: 'unique_view',
        dataIndex: '[5]',
        render: (text, record) => <span>{record[5]?.toLocaleString()}</span>
      },
      {
        title: (
          <TableHeader
            title="Engagement Rate"
            tooltip="Total number of Referrals campaign engagements"
          />
        ),
        key: 'engagement_rate',
        dataIndex: '[6]',
        render: (text, record) => <span>{record[6]?.toLocaleString()}</span>
      },
      {
        title: <TableHeader title="Total referral" tooltip="Total number of Referrals campaigns" />,
        key: 'total_referral',
        dataIndex: '[7]',
        render: (text, record) => <span>{record[7]?.toLocaleString()}</span>
      }
    ]
  };
  return (
    <>
      <CampaignTableGraph
        identifier="campaign_referral_top_campaign_by_performance"
        title="Performance"
        pagination={true}
        tableProps={tableProps}
        scrollX={true}
        filterParams={filterParams}
      />
    </>
  );
};

const ReferralsStateBI = ({ filterParams = {} }) => {
  const tableProps: TableProps<any> = {
    rowKey: (r: any) => r[0],
    // scroll: { x: 'scroll' },
    columns: [
      {
        title: 'State Name',
        key: 'primary_cluster_state',
        dataIndex: '[3]'
      },
      {
        title: (
          <TableHeader
            title="Engagements"
            tooltip="Total number of Referrals campaign engagements"
          />
        ),
        key: 'issued_count',
        dataIndex: '[1]',
        render: (text, record) => <span>{record[1]?.toLocaleString()}</span>
      },
      {
        title: (
          <TableHeader
            title="Completions"
            tooltip="Total number of Referrals campaign completions"
          />
        ),
        key: 'completed_count',
        dataIndex: '[2]',
        render: (text, record) => <span>{record[2]?.toLocaleString()}</span>
      }
    ]
  };
  return (
    <>
      <CampaignTableGraph
        identifier="campaign_referral_state"
        title="Location - State"
        minHeight="320px"
        tableProps={tableProps}
        pagination={true}
        filterParams={filterParams}
      />
    </>
  );
};

const ReferralsClusterBI = ({ filterParams = {} }) => {
  const tableProps: TableProps<any> = {
    rowKey: (r: any) => r[0],
    columns: [
      {
        title: 'Cluster Name',
        key: 'primary_cluster',
        dataIndex: '[3]'
      },
      {
        title: (
          <TableHeader
            title="Engagements"
            tooltip="Total number of Referrals campaign engagements"
          />
        ),
        key: 'issued_count',
        dataIndex: '[1]',
        render: (text, record) => <span>{record[1]?.toLocaleString()}</span>
      },
      {
        title: (
          <TableHeader
            title="Completions"
            tooltip="Total number of Referrals campaign completions"
          />
        ),
        key: 'completed_count',
        dataIndex: '[2]',
        render: (text, record) => <span>{record[1]?.toLocaleString()}</span>
      }
    ]
  };
  return (
    <>
      <CampaignTableGraph
        identifier="campaign_referral_cluster"
        title="Location - Cluster"
        minHeight="320px"
        tableProps={tableProps}
        pagination={true}
        filterParams={filterParams}
      />
    </>
  );
};

interface TableHeaderProps {
  title: string;
  tooltip?: string;
}

const TableHeader = ({ title, tooltip }: TableHeaderProps) => {
  return <>{title}</>;
};

export default memo(Referrals);
